import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import type { MethodologyResponse } from '@/api/schema/data-contracts';
import type { OmitSafely } from '@/types';

import type { EstimationItemsGroupedTableProps } from '../../EstimationItemsGroupedTables';
import EstimationItemsGroupedTables from '../../EstimationItemsGroupedTables';
import { mapFromResourceEstimationItemsTableRecord } from '../../mappers/estimationItemsEditFormModelMapper';
import type { EstimationItemsEditFormModel, ResourceEstimationItemsTableRecord } from '../../model';

interface TopLevelEstimationItemsFormProps {
  topLevelEstimationItems: ResourceEstimationItemsTableRecord[];
  methodologies: MethodologyResponse[];
  estimationMethodologyId: string;
  tableProps: OmitSafely<
    EstimationItemsGroupedTableProps,
    | 'estimationItems'
    | 'control'
    | 'onIncludeClicked'
    | 'onMethodologyChange'
    | 'formModelEstimationItems'
  >;
}

export const TopLevelEstimationItemsForm: FC<TopLevelEstimationItemsFormProps> = ({
  topLevelEstimationItems,
  estimationMethodologyId,
  tableProps,
}) => {
  const formModelInitial = useMemo<EstimationItemsEditFormModel>(
    () => ({
      estimationItems: topLevelEstimationItems.map((x) =>
        mapFromResourceEstimationItemsTableRecord(
          x,
          estimationMethodologyId,
          tableProps.estimationModel,
        ),
      ),
    }),
    [topLevelEstimationItems, tableProps.estimationModel, estimationMethodologyId],
  );

  const { control } = useForm<EstimationItemsEditFormModel>({
    mode: 'onChange',
    defaultValues: formModelInitial,
  });

  const { showEstimateColumn: _showEstimateColumn, ...restTableProps } = tableProps;

  return (
    <EstimationItemsGroupedTables
      control={control}
      formModelEstimationItems={formModelInitial.estimationItems}
      estimationItems={topLevelEstimationItems}
      showEstimateColumn={true}
      {...restTableProps}
    />
  );
};
